<script>
import { mapGetters } from 'vuex';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import { extractVariablesFromCannedResponse } from '@/dashboard/helper/cannedResponseVariableHelper';

export default {
  name: 'ResponseTemplateModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    conversation: {
      type: Object,
      default: () => ({}),
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: 'normal', // 'normal' or 'facebook_private_reply'
    },
    message: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'send', 'sendPrivateReply'],
  data() {
    return {
      searchQuery: '',
      selectedTemplate: null,
      variableValues: {},
      showVariableForm: false,
    };
  },
  computed: {
    ...mapGetters('cannedResponse', {
      allTemplates: 'getCannedResponses',
    }),

    filteredTemplates() {
      const query = this.searchQuery.toLowerCase();
      return (this.allTemplates || []).filter(template => {
        const matchesSearch =
          !query ||
          template.short_code.toLowerCase().includes(query) ||
          (template.content && template.content.toLowerCase().includes(query));

        // For Facebook Private Reply mode, only show text templates
        if (this.isPrivateReplyMode) {
          return matchesSearch && template.content_type === 'text';
        }

        // Filter by channel compatibility
        const channelType = this.inbox?.channel_type;

        // Text templates work everywhere
        if (template.content_type === 'text') {
          return matchesSearch;
        }

        // Interactive templates for WhatsApp
        if (channelType === 'Channel::Whatsapp') {
          const whatsappTypes = [
            'input_select',
            'catalog_product',
            'catalog_product_list',
            'flow',
            'location',
            'url_button',
          ];
          if (whatsappTypes.includes(template.content_type)) {
            return matchesSearch;
          }
        }

        // Interactive templates for Web Chat
        if (
          channelType === 'Channel::WebWidget' ||
          channelType === 'Channel::Api'
        ) {
          const webTypes = ['cards', 'input_select'];
          if (webTypes.includes(template.content_type)) {
            return matchesSearch;
          }
        }

        // Multimedia works everywhere
        if (template.content_type === 'multimedia') {
          return matchesSearch;
        }

        return false;
      });
    },

    messageVariables() {
      return getMessageVariables({
        conversation: this.conversation,
        contact: this.contact,
        inbox: this.inbox,
      });
    },

    templateVariables() {
      if (!this.selectedTemplate) return [];

      // Extract ALL variables from content AND content_attributes (buttons, cards, etc.)
      const allVariables = extractVariablesFromCannedResponse(
        this.selectedTemplate
      );
      console.log('🔍 [DEBUG] Extracted all variables:', allVariables);
      console.log('🔍 [DEBUG] System variables:', this.messageVariables);

      // Filter to only undefined/custom variables (not system variables)
      const undefinedVars = allVariables.filter(varName => {
        return !this.messageVariables.hasOwnProperty(varName);
      });
      console.log(
        '🔍 [DEBUG] Custom variables (to prompt for):',
        undefinedVars
      );

      return undefinedVars;
    },

    hasVariables() {
      return this.templateVariables.length > 0;
    },

    channelTypeLabel() {
      const channelType = this.inbox?.channel_type;
      if (channelType === 'Channel::Whatsapp') return 'WhatsApp';
      if (channelType === 'Channel::WebWidget') return 'Web Chat';
      if (channelType === 'Channel::Api') return 'API';
      if (channelType === 'Channel::Email') return 'Email';
      if (channelType === 'Channel::Sms') return 'SMS';
      return 'All Channels';
    },
    isPrivateReplyMode() {
      return this.mode === 'facebook_private_reply';
    },
    sendButtonText() {
      return this.isPrivateReplyMode ? 'Send Private Reply' : 'Send Template';
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.fetchTemplates();
        this.resetSelection();
      }
    },
  },
  mounted() {
    if (this.show) {
      this.fetchTemplates();
    }
  },
  methods: {
    fetchTemplates() {
      this.$store.dispatch('cannedResponse/getCannedResponse', {
        searchKey: '',
      });
    },

    resetSelection() {
      this.selectedTemplate = null;
      this.variableValues = {};
      this.showVariableForm = false;
      this.searchQuery = '';
    },

    selectTemplate(template) {
      this.selectedTemplate = template;

      // If template has variables, show variable form
      if (this.hasVariables) {
        this.showVariableForm = true;
        // Initialize variable values (Vue 3 compatible)
        const newVariableValues = {};
        this.templateVariables.forEach(varName => {
          newVariableValues[varName] = '';
        });
        this.variableValues = newVariableValues;
      } else {
        // No variables, send directly
        this.sendTemplate();
      }
    },

    getTemplateTypeLabel(contentType) {
      const labels = {
        text: 'Text',
        input_select: 'Interactive',
        cards: 'Cards',
        catalog_product: 'Product',
        catalog_product_list: 'Product List',
        flow: 'Flow',
        location: 'Location',
        url_button: 'URL Button',
        multimedia: 'Media',
      };
      return labels[contentType] || contentType;
    },

    async sendTemplate() {
      if (!this.selectedTemplate) return;

      console.log('🔍 [DEBUG] sendTemplate called');
      console.log('🔍 [DEBUG] Template:', this.selectedTemplate);
      console.log('🔍 [DEBUG] Variable values:', this.variableValues);
      console.log('🔍 [DEBUG] Mode:', this.mode);

      // For private reply mode, send canned response ID with variables
      if (this.isPrivateReplyMode) {
        console.log('🚀 [Modal] Emitting sendPrivateReply event with:', {
          cannedResponseId: this.selectedTemplate.id,
          variableValues: this.variableValues,
        });
        this.$emit('sendPrivateReply', {
          cannedResponseId: this.selectedTemplate.id,
          variableValues: this.variableValues,
        });
        console.log('✅ [Modal] Event emitted, now closing modal');
        this.closeModal();
        return;
      }

      let messageData = {};

      // Replace variables in content for ALL template types
      let content = this.selectedTemplate.content || '';

      // Replace custom variables first
      Object.keys(this.variableValues).forEach(varName => {
        console.log(
          `🔍 [DEBUG] Replacing {{${varName}}} with "${this.variableValues[varName]}"`
        );
        const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'g');
        const beforeReplace = content;
        content = content.replace(regex, this.variableValues[varName]);
        console.log(
          `🔍 [DEBUG] Content before: "${beforeReplace}", after: "${content}"`
        );
      });

      // Then replace system variables
      content = replaceVariablesInMessage({
        message: content,
        variables: this.messageVariables,
      });

      if (this.selectedTemplate.content_type === 'text') {
        // For text templates, just send the processed content
        messageData = {
          message: content,
        };
      } else if (this.selectedTemplate.content_type === 'multimedia') {
        // For multimedia templates, decode base64 files and create File objects
        const attachmentFiles =
          this.selectedTemplate.content_attributes?.attachment_files || [];

        if (attachmentFiles.length === 0) {
          // No files, just send as text
          messageData = {
            message: content,
          };
        } else {
          // Decode base64 files and create File objects
          const files = await Promise.all(
            attachmentFiles.map(async fileData => {
              // Decode base64 to binary
              const base64Data = fileData.data;
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }

              // Create Blob from binary data
              const blob = new Blob([bytes], { type: fileData.type });

              // Create File object
              return new File([blob], fileData.name, { type: fileData.type });
            })
          );

          messageData = {
            message: content,
            files: files,
            private: 0,
          };
        }
      } else {
        // For other interactive templates, send with content_type and attributes
        // Also need to replace variables in content_attributes
        let contentAttributes = JSON.parse(
          JSON.stringify(this.selectedTemplate.content_attributes || {})
        );

        // Replace variables in content_attributes recursively
        console.log(
          '🔍 [DEBUG] Original contentAttributes:',
          JSON.stringify(contentAttributes, null, 2)
        );

        const replaceInObject = obj => {
          for (const key in obj) {
            if (typeof obj[key] === 'string') {
              const originalValue = obj[key];
              // Replace custom variables
              Object.keys(this.variableValues).forEach(varName => {
                console.log(
                  `🔍 [DEBUG] Checking field "${key}" = "${obj[key]}" for {{${varName}}}`
                );
                const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'g');
                const before = obj[key];
                obj[key] = obj[key].replace(
                  regex,
                  this.variableValues[varName]
                );
                if (before !== obj[key]) {
                  console.log(
                    `🔍 [DEBUG] ✅ Replaced in "${key}": "${before}" → "${obj[key]}"`
                  );
                }
              });
              // Replace system variables
              obj[key] = replaceVariablesInMessage({
                message: obj[key],
                variables: this.messageVariables,
              });
            } else if (Array.isArray(obj[key])) {
              obj[key].forEach(item => {
                if (typeof item === 'object') replaceInObject(item);
              });
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
              replaceInObject(obj[key]);
            }
          }
        };

        replaceInObject(contentAttributes);

        console.log(
          '🔍 [DEBUG] Final contentAttributes:',
          JSON.stringify(contentAttributes, null, 2)
        );

        // Remove channel_type from content_attributes as it's metadata, not message content
        delete contentAttributes.channel_type;

        // For input_select (list messages), remove empty description fields
        if (this.selectedTemplate.content_type === 'input_select') {
          contentAttributes.sections?.forEach(section => {
            section.rows?.forEach(row => {
              if (
                row.description === '' ||
                row.description === null ||
                row.description === undefined
              ) {
                delete row.description;
              }
            });
          });
        }

        messageData = {
          message: content,
          content_type: this.selectedTemplate.content_type,
          content_attributes: contentAttributes,
        };
      }

      console.log(
        '🔍 [DEBUG] Final messageData being emitted:',
        JSON.stringify(messageData, null, 2)
      );
      this.$emit('send', messageData);
      this.closeModal();
    },

    closeModal() {
      this.resetSelection();
      this.$emit('close');
    },

    goBack() {
      this.showVariableForm = false;
      this.selectedTemplate = null;
      this.variableValues = {};
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12">
            {{
              showVariableForm
                ? 'Fill Template Variables'
                : 'Select Response Template'
            }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-1">
            {{
              showVariableForm
                ? 'Fill in the required information'
                : isPrivateReplyMode
                  ? 'Select a text template for Facebook Private Reply'
                  : `Available templates for ${channelTypeLabel}`
            }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12"
          @click="closeModal"
        >
          <fluent-icon icon="dismiss" size="24" />
        </button>
      </div>

      <!-- Template Selection View -->
      <div v-if="!showVariableForm" class="flex flex-col flex-1">
        <!-- Search -->
        <div class="p-4 border-b border-n-weak">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search templates by name or content..."
            class="w-full px-4 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>

        <!-- Template List -->
        <div class="flex-1 overflow-y-auto p-4 max-h-96">
          <div v-if="filteredTemplates.length === 0" class="text-center py-8">
            <fluent-icon
              icon="search"
              size="48"
              class="text-n-slate-9 mx-auto mb-4"
            />
            <p class="text-n-slate-11">
              {{
                searchQuery
                  ? 'No templates found matching your search'
                  : 'No templates available for this channel'
              }}
            </p>
          </div>

          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="p-4 mb-3 border border-n-weak rounded-lg hover:border-n-brand cursor-pointer transition-all"
            @click="selectTemplate(template)"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <h3 class="font-semibold text-n-slate-12">
                    {{ template.short_code }}
                  </h3>
                  <span
                    class="px-2 py-0.5 text-xs rounded-full bg-n-brand/10 text-n-brand font-medium"
                  >
                    {{ getTemplateTypeLabel(template.content_type) }}
                  </span>
                </div>
                <p class="text-sm text-n-slate-11 mt-1 line-clamp-2">
                  {{ template.content || 'Interactive message template' }}
                </p>
              </div>
              <fluent-icon
                icon="chevron-right"
                size="20"
                class="text-n-slate-9"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Variable Form View -->
      <div v-else class="flex flex-col flex-1 p-6">
        <div class="mb-4 p-4 bg-n-slate-2 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <h3 class="font-semibold text-n-slate-12">
              {{ selectedTemplate.short_code }}
            </h3>
            <span
              class="px-2 py-0.5 text-xs rounded-full bg-n-brand/10 text-n-brand font-medium"
            >
              {{ getTemplateTypeLabel(selectedTemplate.content_type) }}
            </span>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ selectedTemplate.content }}
          </p>
        </div>

        <div class="space-y-4">
          <div
            v-for="varName in templateVariables"
            :key="varName"
            class="flex flex-col"
          >
            <label class="text-sm font-medium text-n-slate-12 mb-2">
              {{ varName }}
            </label>
            <input
              v-model="variableValues[varName]"
              type="text"
              :placeholder="`Enter value for ${varName}`"
              class="px-4 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button
            class="flex-1 px-4 py-2 border border-n-weak rounded-lg hover:bg-n-slate-3 transition-colors"
            @click="goBack"
          >
            Back
          </button>
          <button
            class="flex-1 px-4 py-2 bg-n-brand text-white rounded-lg hover:brightness-110 transition-all"
            @click="sendTemplate"
          >
            {{ sendButtonText }}
          </button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
